import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cx from 'classnames';
import {
  AnimatedDiv,
  ShowAnimation,
} from '@vimily/bonjoro-ui-components-v2/AnimatedDiv';
import { assetUrl } from '~/helpers';
import { InputGroup } from './InputGroup';
import { FormProvider, useForm } from 'react-hook-form';
import { AppSumoCodeList } from './AppSumoCodeList';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import CsrfToken from '~/App/Components/CsrfToken';
import { isEmail } from '~/utils/String';
import { SignUpNotice } from './SignUpNotice';

export interface AppSumoFormArgs {
  first_name?: string;
  last_name?: string;
  email?: string;
  password?: string;
  appsumo_codes?: string[];
}

declare global {
  interface Window {
    formInputs: AppSumoFormArgs;
    formErrors: Record<string, string[]>;
  }
}

export enum ErrorType {
  Manual = 'manual',
}

export function App() {
  const formRef = useRef<HTMLFormElement>(null);
  const recaptchaRef = useRef<HTMLInputElement>(
    document.getElementById('recaptcha') as HTMLInputElement
  );
  const [recaptcha, setRacaptcha] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  const methods = useForm<AppSumoFormArgs>();

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { handleSubmit, setError, setValue } = methods;

  useEffect(() => {
    const fieldsToFill = ['first_name', 'last_name', 'email'];

    fieldsToFill.forEach((field) => {
      const value = window.formInputs[field] as string;
      const error = window.formErrors?.[field]?.[0];

      if (value) {
        setValue(field, window.formInputs[field]);
      }
      if (error) {
        setError(field, {
          type: ErrorType.Manual,
          message: error,
        });
      }
    });
  }, []);

  /**
   * Checks every 1 sec if the Recaptcha rendered outside the React form has already a value.
   * Once there is a value, use it to set the Recaptcha input inside the form
   */
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (!recaptchaRef?.current || recaptcha) return;

      if (recaptchaRef?.current.value) {
        setRacaptcha(recaptchaRef?.current.value);
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [recaptcha, recaptchaRef?.current]);

  const updateClickHandler = useCallback(() => {
    if (!formRef.current || !recaptchaRef.current) return;

    setSubmitting(true);
    setRacaptcha(recaptchaRef.current.value);

    formRef.current.submit();
  }, [formRef, recaptchaRef]);

  const emailValidation = (value: string) => {
    if (!isEmail(value)) {
      return 'The email must be a valid email address.';
    }

    return true;
  };

  const passwordValidation = (value: string) => {
    if (value.length < 8) {
      return 'The password must be at least 8 characters.';
    }

    return true;
  };

  const initialCodes = window.formInputs?.appsumo_codes ?? [];
  const formErrors = window.formErrors;

  return (
    <FormProvider {...methods}>
      <form
        ref={formRef}
        action='/auth/register/appsumo'
        method='post'
        onSubmit={handleSubmit(updateClickHandler)}
        className={cx([
          'w-full h-full v2-bg-magenta-100 block v2-animate-fade-in-150 relative',
          'flex inset-0 transition overflow-y-scroll p-6',
        ])}
      >
        <CsrfToken />
        <input name='recaptcha' type='hidden' value={recaptcha} />
        <AnimatedDiv
          show
          showAnimation={ShowAnimation.SlideInRight}
          className='v2-bg-white rounded-md shadow-md drop-shadow-md w-[550px] h-auto block relative flex flex-col m-auto p-6 min-[550px]:p-12 gap-y-4'
        >
          <div className='flex flex-col min-[550px]:flex-row gap-x-6 gap-y-3 justify-center items-center'>
            <img
              src={assetUrl('/assets/images/logos/logo_full.png')}
              className='w-[188px] h-auto transition'
            />
            <span className='w-[45px] h-[3px] min-[550px]:w-[3px] min-[550px]:h-[45px] v2-bg-neutral-300' />
            <img
              src={assetUrl('/assets/images/logos/appsumo-logo.svg')}
              className='w-[188px] h-auto transition scale-125'
            />
          </div>
          <div className='flex flex-col gap-y-4 pt-2'>
            <div className='flex flex-col min-[400px]:flex-row gap-x-6 gap-y-6'>
              <InputGroup
                name='first_name'
                label='First name'
                placeholder='John'
              />
              <InputGroup
                name='last_name'
                label='Last name'
                placeholder='Doe'
              />
            </div>
            <InputGroup
              name='email'
              label='Work email'
              placeholder='email@companyname.com'
              validate={emailValidation}
            />
            <InputGroup
              name='password'
              label='Password'
              type='password'
              placeholder='Enter password'
              validate={passwordValidation}
            />
            <AppSumoCodeList
              disabled={submitting}
              initialCodes={initialCodes}
              formErrors={formErrors}
            />
            <SignUpNotice />
            <Button
              variant={ButtonVariant.Primary}
              className='text-[21px] font-bold'
              disabled={submitting}
            >
              Let&apos;s go
              {submitting && <span className='fa fa-spinner spinning ml-2' />}
            </Button>
          </div>
        </AnimatedDiv>
      </form>
    </FormProvider>
  );
}

export default function AppSumoRegistration() {
  const target = document.getElementById('react-target');

  if (!target) return null;

  target.innerHTML = '';

  return createPortal(<App />, target);
}
